import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import type { OpenCustomerDetail } from '../navigation/uiActions';
import {
    Search,
    X,
    LayoutGrid,
    Globe,
    AlertTriangle,
    CheckCircle2,
    XCircle,
    Users,
    AlertOctagon,
    ListTodo,
    Mail,
    FileText,
    HandMetal as Handshake,
    Inbox,
    Megaphone,
} from 'lucide-react';
import type { Customer } from '../api';
import { fetchCustomers } from '../api';
import { CustomerCard } from '../components/CustomerCard';
import { ActivityLog } from '../components/ActivityLog';
import { PendingApprovals } from '../components/PendingApprovals';
import { Kundprofil } from '../components/Kundprofil';
import { useSynligtIntervall } from '../hooks/useSynligtIntervall';

// Lazy-load Realm3D to prevent Three.js version mismatch from crashing entire app
const Realm3D = lazy(() => import('../components/Realm3D').then(m => ({ default: m.Realm3D })));

/* ─── Config ─── */
const STATUS_PRIORITY: Record<string, number> = {
    error: 0,
    warning: 1,
    active: 2,
};

const FILTER_CONFIG: Record<string, { icon: React.ReactNode; label: string }> = {
    all: { icon: <Users size={13} />, label: 'Alla' },
    error: { icon: <XCircle size={13} />, label: 'Kritisk' },
    warning: { icon: <AlertTriangle size={13} />, label: 'Varning' },
    active: { icon: <CheckCircle2 size={13} />, label: 'Aktiv' },
};

type ViewMode = 'cards' | '3d';
type DetailTab = 'overview' | 'contact' | 'website' | 'ads' | 'agreements' | 'documents';

const DETAIL_TABS: { key: DetailTab; icon: React.ReactNode; label: string }[] = [
    { key: 'overview', icon: <LayoutGrid size={14} />, label: 'Översikt' },
    { key: 'contact', icon: <Mail size={14} />, label: 'Kontakt' },
    // Visas bara för kunder som har en spårad sajt (SCC-51).
    { key: 'website', icon: <Globe size={14} />, label: 'Hemsida' },
    // Visas bara för kunder med ett Meta-annonskonto i config (SCC-58).
    { key: 'ads', icon: <Megaphone size={14} />, label: 'Annonser' },
    { key: 'agreements', icon: <Handshake size={14} />, label: 'Avtal' },
    { key: 'documents', icon: <FileText size={14} />, label: 'Dokument' },
];

const WebsiteView = lazy(() => import('./WebsiteView'));
const AdsView = lazy(() => import('./AdsView'));

interface Props {
    onTaskCreated: () => void;
}

export function CustomerView({ onTaskCreated }: Props) {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [refreshKey, setRefreshKey] = useState(0);
    const [detailOpen, setDetailOpen] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>('cards');
    const [detailTab, setDetailTab] = useState<DetailTab>('overview');

    const loadCustomers = useCallback(async () => {
        try {
            const data = await fetchCustomers();
            setCustomers(data);
        } catch (err) {
            console.error('Failed to fetch customers:', err);
        }
        setLoading(false);
    }, []);

        useEffect(() => { loadCustomers(); }, [loadCustomers]);
        useSynligtIntervall(loadCustomers, 30000);

    const handleRefresh = useCallback(() => {
        setRefreshKey(prev => prev + 1);
    }, []);

    const sortedCustomers = useMemo(() => {
        let result = [...customers];
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(c =>
                c.name.toLowerCase().includes(q) ||
                c.slug.toLowerCase().includes(q)
            );
        }
        if (statusFilter !== 'all') {
            result = result.filter(c => c.status === statusFilter);
        }
        result.sort((a, b) => {
            const pa = STATUS_PRIORITY[a.status] ?? 99;
            const pb = STATUS_PRIORITY[b.status] ?? 99;
            if (pa !== pb) return pa - pb;
            return b.errors_24h - a.errors_24h;
        });
        return result;
    }, [customers, searchQuery, statusFilter]);

    const handleSelectCustomer = (id: string | null) => {
        if (selectedCustomerId === id) {
            setSelectedCustomerId(null);
            setDetailOpen(false);
        } else {
            setSelectedCustomerId(id);
            setDetailOpen(true);
            setDetailTab('overview');
        }
    };

    const selectedCustomer = customers.find(c => c.id === selectedCustomerId);

    // Alex (navigate_ui): öppna en kund på en viss flik. Kundlistan är alltid
    // laddad i bakgrunden (30 s-poll), så id:t räcker; Hemsida-fliken faller
    // tillbaka till Översikt för kunder utan spårad sajt.
    useEffect(() => {
        const onOpenCustomer = (e: Event) => {
            const d = (e as CustomEvent<OpenCustomerDetail>).detail;
            if (!d?.customerId) return;
            const c = customers.find(x => x.id === d.customerId);
            const wanted = DETAIL_TABS.some(t => t.key === d.tab) ? (d.tab as DetailTab) : 'overview';
            const otillganglig = (wanted === 'website' && !c?.site_tenant_slug)
                || (wanted === 'ads' && !c?.meta_ad_account_id);
            const tab: DetailTab = otillganglig ? 'overview' : wanted;
            setSelectedCustomerId(d.customerId);
            setDetailOpen(true);
            setDetailTab(tab);
        };
        window.addEventListener('scc:open-customer', onOpenCustomer);
        return () => window.removeEventListener('scc:open-customer', onOpenCustomer);
    }, [customers]);

    // Vilket filter aktivitetsloggen visar. Ligger här och inte i ActivityLog, för
    // att Fel- och Varningsrutorna ovanför loggen ska kunna styra det.
    const [aktivitetsFilter, setAktivitetsFilter] = useState('all');

    const statusCounts = useMemo(() => ({
        all: customers.length,
        error: customers.filter(c => c.status === 'error').length,
        warning: customers.filter(c => c.status === 'warning').length,
        active: customers.filter(c => c.status === 'active').length,
    }), [customers]);

    return (
        <div className="customer-view">
            {/* Toolbar */}
            <div className="cv-toolbar">
                <div className="cv-search-wrap">
                    <Search size={14} className="cv-search-icon" />
                    <input
                        className="cv-search"
                        type="text"
                        placeholder="Sök kund…"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="cv-status-filters">
                    {(['all', 'error', 'warning', 'active'] as const).map(status => (
                        <button
                            key={status}
                            className={`cv-filter-btn ${statusFilter === status ? 'active' : ''}`}
                            onClick={() => setStatusFilter(status)}
                        >
                            {FILTER_CONFIG[status].icon}
                            {FILTER_CONFIG[status].label}
                            <span className="cv-filter-count">{statusCounts[status]}</span>
                        </button>
                    ))}
                </div>
                <div className="cv-view-toggle">
                    <button
                        className={`cv-view-btn ${viewMode === 'cards' ? 'active' : ''}`}
                        onClick={() => setViewMode('cards')}
                        title="Kortvy"
                    >
                        <LayoutGrid size={15} />
                    </button>
                    <button
                        className={`cv-view-btn ${viewMode === '3d' ? 'active' : ''}`}
                        onClick={() => setViewMode('3d')}
                        title="3D Realm"
                    >
                        <Globe size={15} />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="cv-body">
                {viewMode === 'cards' ? (
                    <div className={`cv-cards-area ${detailOpen ? 'with-detail' : ''}`}>
                        {loading ? (
                            <div className="cv-loading">Laddar kunder…</div>
                        ) : sortedCustomers.length === 0 ? (
                            <div className="cv-empty">
                                <Inbox size={28} strokeWidth={1.5} />
                                <p>Inga kunder matchar filtret</p>
                            </div>
                        ) : (
                            <div className="cv-cards-grid">
                                {sortedCustomers.map(c => (
                                    <CustomerCard
                                        key={c.id}
                                        customer={c}
                                        isSelected={selectedCustomerId === c.id}
                                        onClick={() => handleSelectCustomer(c.id)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="cv-realm-area">
                        <Suspense fallback={<div className="cv-loading">Laddar 3D…</div>}>
                            <Realm3D
                                selectedCustomerId={selectedCustomerId}
                                onSelectCustomer={handleSelectCustomer}
                            />
                        </Suspense>
                    </div>
                )}

                {/* ─── Tabbed Detail Panel ─── */}
                <div className={`cv-detail-panel ${detailOpen && selectedCustomer ? 'open' : ''}`}>
                    {selectedCustomer && (
                        <>
                            <div className="cv-detail-header">
                                <div className="cv-detail-title-row">
                                    <h2>{selectedCustomer.name}</h2>
                                    <button
                                        className="cv-detail-close"
                                        onClick={() => {
                                            setDetailOpen(false);
                                            setSelectedCustomerId(null);
                                        }}
                                        aria-label="Stäng"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                                <span className="cv-detail-slug">{selectedCustomer.slug}</span>
                            </div>

                            {/* Tab Navigation */}
                            <div className="cv-detail-tabs">
                                {DETAIL_TABS.filter(tab =>
                                    (tab.key !== 'website' || selectedCustomer?.site_tenant_slug) &&
                                    (tab.key !== 'ads' || selectedCustomer?.meta_ad_account_id)
                                ).map(tab => (
                                    <button
                                        key={tab.key}
                                        className={`cv-detail-tab ${detailTab === tab.key ? 'active' : ''}`}
                                        onClick={() => setDetailTab(tab.key)}
                                    >
                                        {tab.icon}
                                        <span>{tab.label}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Tab Content */}
                            <div className="cv-detail-content">
                                {detailTab === 'overview' && (
                                    <>
                                        <div className="cv-detail-stats">
                                            {/* Fel och varningar filtrerar aktivitetsloggen. Klick på en ruta
                                                som redan är vald nollställer tillbaka till Alla. */}
                                            <button
                                                type="button"
                                                className={`cv-detail-stat cv-detail-stat--klickbar ${aktivitetsFilter === 'error' ? 'ar-vald' : ''}`}
                                                onClick={() => setAktivitetsFilter(f => f === 'error' ? 'all' : 'error')}
                                                title="Visa bara fel i aktivitetsloggen"
                                            >
                                                <AlertOctagon size={14} className="cv-ds-icon cv-ds-error-icon" />
                                                <div className="cv-ds-info">
                                                    <span className="cv-ds-label">Fel (24h)</span>
                                                    <span className="cv-ds-value cv-ds-error">{selectedCustomer.errors_24h}</span>
                                                </div>
                                            </button>
                                            <button
                                                type="button"
                                                className={`cv-detail-stat cv-detail-stat--klickbar ${aktivitetsFilter === 'warn' ? 'ar-vald' : ''}`}
                                                onClick={() => setAktivitetsFilter(f => f === 'warn' ? 'all' : 'warn')}
                                                title="Visa bara varningar i aktivitetsloggen"
                                            >
                                                <AlertTriangle size={14} className="cv-ds-icon cv-ds-warn-icon" />
                                                <div className="cv-ds-info">
                                                    <span className="cv-ds-label">Varningar (24h)</span>
                                                    <span className="cv-ds-value cv-ds-warn">{selectedCustomer.warnings_24h}</span>
                                                </div>
                                            </button>
                                            <div className="cv-detail-stat">
                                                <ListTodo size={14} className="cv-ds-icon cv-ds-tasks-icon" />
                                                <div className="cv-ds-info">
                                                    <span className="cv-ds-label">Öppna uppgifter</span>
                                                    <span className="cv-ds-value">{selectedCustomer.open_tasks}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="cv-detail-section">
                                            <ActivityLog
                                                key={`act-${refreshKey}`}
                                                selectedCustomerId={selectedCustomerId}
                                                filter={aktivitetsFilter}
                                                onFilterChange={setAktivitetsFilter}
                                            />
                                        </div>

                                        <div className="cv-detail-section">
                                            <PendingApprovals
                                                key={`approvals-${refreshKey}`}
                                                selectedCustomerId={selectedCustomerId}
                                                onApproved={() => {
                                                    handleRefresh();
                                                    onTaskCreated();
                                                }}
                                            />
                                        </div>
                                    </>
                                )}

                                {detailTab === 'contact' && selectedCustomerId && (
                                    <Kundprofil customerId={selectedCustomerId} vy="kontakt" />
                                )}

                                {detailTab === 'website' && (
                                    selectedCustomer?.site_tenant_slug ? (
                                        <Suspense fallback={<div className="cv-detail-empty-tab"><p>Laddar hemsidedata…</p></div>}>
                                            <WebsiteView tenant={selectedCustomer.site_tenant_slug} />
                                        </Suspense>
                                    ) : (
                                        <div className="cv-detail-empty-tab">
                                            <Globe size={24} strokeWidth={1.5} />
                                            <p>Ingen sajt kopplad till kunden</p>
                                        </div>
                                    )
                                )}

                                {detailTab === 'ads' && (
                                    selectedCustomer?.meta_ad_account_id ? (
                                        <Suspense fallback={<div className="cv-detail-empty-tab"><p>Laddar annonsdata…</p></div>}>
                                            <AdsView slug={selectedCustomer.slug} />
                                        </Suspense>
                                    ) : (
                                        <div className="cv-detail-empty-tab">
                                            <Megaphone size={24} strokeWidth={1.5} />
                                            <p>Inget annonskonto kopplat till kunden</p>
                                        </div>
                                    )
                                )}

                                {detailTab === 'agreements' && selectedCustomerId && (
                                    <Kundprofil customerId={selectedCustomerId} vy="avtal" />
                                )}

                                {detailTab === 'documents' && (
                                    <div className="cv-detail-empty-tab">
                                        <FileText size={24} strokeWidth={1.5} />
                                        <p>Inga dokument uppladdade</p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
